import { Box, Typography } from '@mui/material';
import { Database, Mail, UserRound } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-admin';

import { createAdminApi, type AdminFamilyDetail, type AdminFamilyInvitation, type AdminFamilyMember } from '@/admin/adminApi';
import { dataScopePath } from '@/admin/data/routes';
import { useAdminQuery } from '@/admin/data/useAdminQuery';
import { ABSENT, babyRoleLabel, formatInstant } from '@/admin/users/accountPresentation';
import { accountDetailPath } from '@/admin/users/routes';
import { useHttpClient } from '@/app/providers';
import {
  AccessDenied,
  Avatar,
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  type DataTableColumn,
} from '@/ui/kit';

import {
  ageLabel,
  describeFamilyEvent,
  eventActorLabel,
  formatDay,
  invitationStatus,
} from './familiesPresentation';

/**
 * Fiche d'une famille : profil minimal, membres, invitations, historique.
 *
 * **Aucune action.** Le role d'un membre est une pastille, jamais un menu ;
 * aucune invitation ne se revoque d'ici : c'est le geste du titulaire dans
 * l'application, et l'administration ne s'y substitue que par une decision
 * produit et juridique (#525). Un bouton absent est la regle, pour `support`
 * comme pour `admin`.
 *
 * Aucun chiffre n'est calcule ici : membres, invitations en attente et age sont
 * ceux du serveur, et une date absente s'ecrit « — ».
 */

const StatTile = ({ label, value }: { readonly label: string; readonly value: string }): ReactElement => (
  <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: 'tokens.surfaceMuted', minWidth: 0 }}>
    <Typography component="dd" sx={{ m: 0, fontSize: 16, fontWeight: 800, overflowWrap: 'anywhere' }}>
      {value}
    </Typography>
    <Typography component="dt" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>
      {label}
    </Typography>
  </Box>
);

/** Adresse d'un compte actif : un lien vers sa fiche. */
const AccountLink = ({ userId, email }: { readonly userId: string; readonly email: string }): ReactElement => (
  <Link to={accountDetailPath(userId)} aria-label={`Ouvrir la fiche de ${email}`} sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
    {email}
  </Link>
);

const memberColumns: readonly DataTableColumn<AdminFamilyMember>[] = [
  {
    key: 'account',
    header: 'Compte',
    primary: true,
    render: (member) => (
      <AccountLink userId={member.userId} email={member.email} />
    ),
  },
  {
    key: 'role',
    header: 'Rôle',
    render: (member) => <Badge label={babyRoleLabel(member.role)} tone={member.role === 'owner' ? 'brand' : 'info'} />,
  },
  { key: 'since', header: 'Depuis', render: (member) => formatDay(member.since) },
];

const InvitationRow = ({ invitation }: { readonly invitation: AdminFamilyInvitation }): ReactElement => {
  const status = invitationStatus(invitation.status);
  const details = [
    babyRoleLabel(invitation.role),
    invitation.invitedByEmail === null ? null : `invitée par ${invitation.invitedByEmail}`,
    `créée le ${formatDay(invitation.createdAt)}`,
    `${invitation.status === 'expired' ? 'expirée' : 'expire'} le ${formatDay(invitation.expiresAt)}`,
  ].filter((part): part is string => part !== null);

  return (
    <Box
      component="li"
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, borderBottom: 1, borderColor: 'tokens.border', '&:last-child': { borderBottom: 0 } }}
    >
      <Box sx={{ color: 'tokens.textMuted', display: 'inline-flex', pt: 0.25 }}>
        <Icon icon={Mail} size={18} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Adresse d'un tiers : rendue telle quelle, jamais presentee comme un compte. */}
        <Typography sx={{ fontSize: 14, fontWeight: 700, overflowWrap: 'anywhere' }}>{invitation.email}</Typography>
        <Typography sx={{ fontSize: 12, color: 'tokens.textMuted' }}>{details.join(' · ')}</Typography>
      </Box>
      <Badge label={status.label} tone={status.tone} dot={invitation.status === 'pending'} />
    </Box>
  );
};

const FamilyContent = ({ family }: { readonly family: AdminFamilyDetail }): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <Avatar name={family.firstName} tone="pink" size={48} />
      <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography component="h2" sx={{ fontSize: 20, fontWeight: 800, overflowWrap: 'anywhere' }}>
            {family.firstName}
          </Typography>
          {family.archived ? <Badge label="Archivé" tone="neutral" /> : <Badge label="Actif" tone="success" dot />}
        </Box>
        <Typography sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
          {ageLabel(family.ageMonths)} · titulaire :{' '}
          {family.owner === null ? ABSENT : <AccountLink userId={family.owner.userId} email={family.owner.email} />}
        </Typography>
      </Box>
      <Button icon={Database} to={dataScopePath(family.id)}>
        Ouvrir dans l’explorateur
      </Button>
    </Box>

    <Box
      component="dl"
      aria-label="Chiffres du profil"
      sx={{ m: 0, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}
    >
      <StatTile label="membres" value={String(family.memberCount)} />
      <StatTile label="invitations en attente" value={String(family.pendingInvitationCount)} />
      <StatTile label="créé le" value={formatDay(family.createdAt)} />
      <StatTile label="archivé le" value={formatDay(family.archivedAt)} />
    </Box>

    <Card headingLevel="h3" title="Membres" subtitle="Rôle de coparentalité, distinct d’un rôle d’administration. Non modifiable ici.">
      <DataTable
        label="Membres du profil"
        columns={memberColumns}
        rows={family.members}
        getRowId={(member) => member.userId}
        emptyMessage="Aucun compte actif n’est rattaché à ce profil."
      />
    </Card>

    <Card
      headingLevel="h3"
      title="Invitations"
      subtitle="Adresses de personnes invitées, qui n’ont pas forcément de compte. L’état est calculé à la lecture."
    >
      {family.invitations.length === 0 ? (
        <Typography sx={{ py: 1, fontSize: 14, color: 'tokens.textMuted' }}>Aucune invitation pour ce profil.</Typography>
      ) : (
        <Box component="ul" aria-label="Invitations du profil" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {family.invitations.map((invitation) => (
            <InvitationRow key={invitation.id} invitation={invitation} />
          ))}
        </Box>
      )}
    </Card>

    <Card headingLevel="h3" title="Historique du partage">
      {family.history.length === 0 ? (
        <Typography sx={{ py: 1, fontSize: 14, color: 'tokens.textMuted' }}>Aucun événement de partage enregistré.</Typography>
      ) : (
        <Box component="ul" aria-label="Historique du partage" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {family.history.map((event) => (
            <Box
              component="li"
              key={event.id}
              sx={{ display: 'flex', gap: 1.5, py: 1, borderBottom: 1, borderColor: 'tokens.border', '&:last-child': { borderBottom: 0 } }}
            >
              <Box sx={{ color: 'tokens.textMuted', display: 'inline-flex', pt: 0.25 }}>
                <Icon icon={UserRound} size={16} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, overflowWrap: 'anywhere' }}>{describeFamilyEvent(event)}</Typography>
                <Typography sx={{ fontSize: 12, color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>
                  par {eventActorLabel(event)} · {formatInstant(event.occurredAt)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
      <Typography sx={{ mt: 1.5, fontSize: 12, color: 'tokens.textMuted' }}>
        50 derniers événements au plus, du plus récent au plus ancien.
      </Typography>
    </Card>
  </Box>
);

export interface FamilyDetailPanelProps {
  readonly babyId: string;
  /** Jeton de relecture : « Actualiser » et le retour sur l'onglet le font avancer. */
  readonly token: number;
  readonly onRetry: () => void;
}

export const FamilyDetailPanel = ({ babyId, token, onRetry }: FamilyDetailPanelProps): ReactElement => {
  const http = useHttpClient();
  const { data, failure } = useAdminQuery(`family:${babyId}`, () => createAdminApi(http).getFamily(babyId), { token });

  if (failure?.anonymized === true) {
    return (
      <AccessDenied title="Compte anonymisé : ce profil n’est plus consultable">
        Plus aucun compte actif n’est rattaché à ce profil. Le serveur ne rend rien de ce qui relève d’un compte anonymisé.
      </AccessDenied>
    );
  }

  if (failure?.notFound === true) {
    return (
      <EmptyState
        title="Profil introuvable"
        description="Aucun profil bébé ne porte cet identifiant sur le serveur. Un profil jamais repris du journal de synchronisation se lit dans l’explorateur de données."
      />
    );
  }

  if (failure !== null && data === null) {
    return failure.forbidden ? (
      <AccessDenied title="Votre rôle ne permet pas de consulter ce profil">
        Votre compte reste connecté : il manque le rôle d’administration requis.
      </AccessDenied>
    ) : (
      <ErrorState message={failure.message} onRetry={onRetry} />
    );
  }

  if (data === null) {
    return <LoadingState label="Chargement du profil…" />;
  }

  return (
    <>
      {failure === null ? null : <ErrorState message={failure.message} onRetry={onRetry} />}
      <FamilyContent family={data} />
    </>
  );
};
